import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { useEffect, useRef, lazy, Suspense, type ReactElement } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import Nav from './components/Nav'
import Hero from './components/Hero'
import HeroBackground from './components/HeroBackground'
import HeroGlobe from './components/HeroGlobe'
import Carriers from './components/Carriers'
import Services from './components/Services'
import Stats from './components/Stats'
import Integrations from './components/Integrations'
import Features from './components/Features'
import FAQ from './components/FAQ'
import Closing from './components/Closing'
import CTA from './components/CTA'
import Footer from './components/Footer'
import SeoHead from './components/SeoHead'
import { orgSchema, websiteSchema, faqPageSchema } from './lib/seo'
import { homeFaqItems } from './data/homeFaq'
import { SITE } from './lib/links'
import { localizedPath } from './i18n/routes'

// Les pages ne sont plus importées en statique : pages/registry les résout au
// rendu. Le prérendu garde bien ses SeoHead par page — entry-server appelle
// warmupAllPages() avant tout renderToString, donc rien ne « suspend » côté SSR.
import { SmoothScroll, getLenis } from './lib/smoothScroll'
import { LocaleProvider, RouteMeta, useT, useLocale } from './i18n/LocaleContext'
import { buildStaticRoutes, buildDynamicRoutes } from './i18n/buildRoutes'
import { PageSlot, STATIC_PAGE_MODULES } from './pages/registry'
import type { StaticPageKey } from './i18n/routes'

// CookieBanner stays lazy — not SEO-critical, loaded after first paint.
const CookieBanner = lazy(() => import('./components/CookieBanner'))

function ScrollToTop() {
  const { pathname, hash } = useLocation()
  const prevPathname = useRef(pathname)
  useEffect(() => {
    // Ancre sur la même page → scroll animé ; arrivée depuis une autre page
    // (ou chargement initial) → positionnement immédiat, comme avant Lenis.
    const samePage = prevPathname.current === pathname
    prevPathname.current = pathname
    const lenis = getLenis()
    if (hash) {
      const id = hash.slice(1)
      requestAnimationFrame(() => {
        const el = document.getElementById(id)
        if (!el) return
        if (lenis) {
          // Lenis ignore scroll-margin-top (que scrollIntoView respecte) :
          // on le reporte manuellement en offset.
          const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0
          lenis.scrollTo(el, { offset: -margin, immediate: !samePage })
        } else {
          el.scrollIntoView({ behavior: 'instant', block: 'start' })
        }
      })
      return
    }
    if (lenis) lenis.scrollTo(0, { immediate: true })
    else window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [pathname, hash])
  return null
}

// Variante du hero de la home — 'map' (historique) ou 'globe' (planète 3D).
const HERO_VARIANT: 'map' | 'globe' = 'globe'

function Landing() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        path={localizedPath('home', locale)}
        jsonLd={[
          orgSchema(locale),
          websiteSchema(locale),
          faqPageSchema(homeFaqItems(t), {
            id: `${SITE.url}/#faq`,
            inLanguage: locale,
          }),
        ]}
      />
      <main className="relative">
        {/* Deux variantes de hero, à basculer ici :
            'map'   → carte du monde en fond + texte centré (historique)
            'globe' → planète WebGL en rotation à droite, texte à gauche */}
        <div className="relative isolate overflow-x-clip">
          {HERO_VARIANT === 'globe' ? (
            <HeroGlobe />
          ) : (
            <>
              <HeroBackground />
              <Hero />
            </>
          )}
          <Carriers />
        </div>
        <Services />
        <Stats />
        <Integrations />
        <Features />
        <Closing />
        <FAQ />
        <CTA />
      </main>
    </>
  )
}

// Mapping pageKey statique → composant de page.
// Seule la home reste importée en statique : c'est la principale page d'entrée,
// et ses sections dominent de toute façon le bundle. Les autres passent par
// PageSlot, qui les résout au rendu (voir pages/registry).
const STATIC_PAGES: Record<StaticPageKey, ReactElement> = Object.fromEntries(
  (Object.keys(STATIC_PAGE_MODULES) as StaticPageKey[]).map((key) => {
    const id = STATIC_PAGE_MODULES[key]
    return [key, id === null ? <Landing /> : <PageSlot id={id} />]
  }),
) as Record<StaticPageKey, ReactElement>

// Routes générées au scope module : l'identité des éléments reste stable
// entre rendus (évite le churn de reconciliation du router).
const APP_ROUTES = [
  ...buildStaticRoutes(STATIC_PAGES),
  ...buildDynamicRoutes('blog', 'blog.article', <PageSlot id="blogArticle" />),
  ...buildDynamicRoutes('sectors', 'sectors.detail', <PageSlot id="sectorDetail" />),
]

export function AppRoutes() {
  return (
    <Routes>
      {APP_ROUTES.map((r) => (
        <Route key={r.path} path={r.path} element={r.element} />
      ))}
      {/* Catch-all 404. pageKey="home" is deliberate: RouteMeta requires a
          non-null PageKey, and it makes the language switcher fall back to the
          localized homepages on an unknown route. */}
      <Route
        path="*"
        element={
          <RouteMeta pageKey="home">
            <PageSlot id="notFound" />
          </RouteMeta>
        }
      />
    </Routes>
  )
}

export function AppShell() {
  return (
    <div className="min-h-screen bg-[#fafafa] text-ink-950 selection:bg-accent-200/60">
      <Nav />
      {/* Au prérendu et à l'hydratation, la page courante est déjà résolue :
          cette frontière ne sert qu'aux navigations client vers une page dont
          le chunk n'est pas encore chargé. */}
      <Suspense fallback={null}>
        <AppRoutes />
      </Suspense>
      <Footer />
      {/* Cookie banner — separate chunk, loaded after first paint */}
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
    </div>
  )
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LocaleProvider>
          <SmoothScroll />
          <ScrollToTop />
          <AppShell />
        </LocaleProvider>
      </BrowserRouter>
    </HelmetProvider>
  )
}
